import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link, useParams } from 'react-router-dom';
import { JiraKeyLink } from '../../components/JiraKeyLink';
import type { EnvLinkRow, EnvLinkStatus } from '../../lib/envLinksApi';
import { useRunStore } from '../../state/runStore';

function statusLabel(status: EnvLinkStatus): string {
  switch (status) {
    case 'ok':
      return 'Ready';
    case 'no-game-task':
      return 'No Game';
    case 'no-recipe':
      return 'No recipe';
    case 'incomplete':
      return 'Incomplete';
    default:
      return status;
  }
}

function statusBadgeClass(status: EnvLinkStatus): string {
  switch (status) {
    case 'ok':
      return 'good';
    case 'no-recipe':
      return 'info';
    case 'no-game-task':
      return 'warn';
    case 'incomplete':
      return 'bad';
    default:
      return 'grey';
  }
}

interface CopyButtonProps {
  text: string;
  label?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Tiny inline clipboard button that flashes "Copied!" on success. Falls back
 * silently when navigator.clipboard isn't available (e.g. http context) so
 * the panel stays usable.
 */
function CopyButton({ text, label, className, disabled }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={className ?? 'btn small'}
      disabled={disabled || !text}
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          setCopied(false);
        }
      }}
    >
      {copied ? 'Copied!' : label ?? 'Copy'}
    </button>
  );
}

/**
 * Pull URL query parameters out of a generated launch URL so we can surface
 * RTP / currency / language as table columns. Most studio templates use
 * `brand` for RTP (e.g. 89/93/96/98) and `language` (or `lang`) for the
 * locale; we look at both. Returns an empty record if the URL is null or
 * malformed - the cells show a dash in that case.
 */
function parseUrlParams(url: string | null): Record<string, string> {
  if (!url) return {};
  try {
    const u = new URL(url);
    const out: Record<string, string> = {};
    u.searchParams.forEach((v, k) => {
      out[k.toLowerCase()] = v;
    });
    return out;
  } catch {
    return {};
  }
}

function rtpFromParams(params: Record<string, string>): string | null {
  return params.brand ?? params.rtp ?? null;
}
function currencyFromParams(params: Record<string, string>): string | null {
  return params.currency ?? null;
}
function langFromParams(params: Record<string, string>): string | null {
  return params.language ?? params.lang ?? null;
}

type StatusFilter = 'all' | 'ready' | 'issues';

interface FiltersState {
  search: string;
  status: StatusFilter;
}

interface EnvLinkTableRowProps {
  row: EnvLinkRow;
  expanded: boolean;
  onToggle: () => void;
}

function EnvLinkTableRow({ row, expanded, onToggle }: EnvLinkTableRowProps) {
  const params = parseUrlParams(row.generatedUrl);
  const url = row.generatedUrl ?? row.playUrl;
  const headerLabel =
    row.resolvedMarket && row.resolvedMarket !== row.jurisdiction
      ? `${row.jurisdiction} (via ${row.resolvedMarket})`
      : row.jurisdiction;
  const rtp = rtpFromParams(params);
  const currency = currencyFromParams(params);
  const lang = langFromParams(params);
  const hasDetail = Boolean(row.guideMd) || row.warnings.length > 0;

  return (
    <>
      <tr className={hasDetail ? 'row-clickable' : ''} onClick={hasDetail ? onToggle : undefined}>
        <td>
          <span className="badge mono small">{headerLabel}</span>
        </td>
        <td>
          <span className={`badge small ${statusBadgeClass(row.status)}`}>
            {statusLabel(row.status)}
          </span>
        </td>
        <td>
          <div className="env-link-cell-stack">
            <span className="env-link-game-name" title={row.gameName ?? undefined}>
              {row.gameName ?? '-'}
            </span>
            {row.gameTaskKey && (
              <JiraKeyLink keyId={row.gameTaskKey} className="badge mono small" />
            )}
          </div>
        </td>
        <td className="mono small">{row.gameId ?? '-'}</td>
        <td className="mono small">{rtp ?? '-'}</td>
        <td className="mono small">{currency ?? '-'}</td>
        <td className="mono small">{lang ?? '-'}</td>
        <td className="env-link-url-cell">
          {url ? (
            <code className="env-link-url-trunc" title={url}>
              {url}
            </code>
          ) : (
            <span className="muted small">No URL</span>
          )}
        </td>
        <td>
          <div className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
            {url && <CopyButton text={url} label="Copy" className="btn small" />}
            {url && (
              <a
                className="btn small"
                href={url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                Open
              </a>
            )}
            {hasDetail && (
              <button
                type="button"
                className="btn small btn-ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
                aria-label={expanded ? 'Hide details' : 'Show details'}
              >
                {expanded ? 'v' : '>'}
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="env-link-detail-row">
          <td colSpan={9}>
            {row.warnings.length > 0 && (
              <ul className="env-link-warnings">
                {row.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
            {row.guideMd && (
              <>
                <div className="env-link-guide">
                  <ReactMarkdown>{row.guideMd}</ReactMarkdown>
                </div>
                <div className="row" style={{ gap: 6, marginTop: 6 }}>
                  <CopyButton text={row.guideMd} label="Copy guide" />
                </div>
              </>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

interface FilterBarProps {
  filters: FiltersState;
  onChange: (next: FiltersState) => void;
  total: number;
  shown: number;
}

function FilterBar({ filters, onChange, total, shown }: FilterBarProps) {
  return (
    <div className="row env-link-filter-bar" style={{ gap: 8, flexWrap: 'wrap' }}>
      <input
        value={filters.search}
        onChange={(e) => onChange({ ...filters, search: e.target.value })}
        placeholder="Filter by market, game, ID, URL..."
        style={{ minWidth: 260, flex: 1 }}
      />
      <div className="row" style={{ gap: 0 }}>
        {(['all', 'ready', 'issues'] as const).map((s) => (
          <button
            key={s}
            type="button"
            className={`btn small ${filters.status === s ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => onChange({ ...filters, status: s })}
            style={{
              borderTopRightRadius: s === 'all' ? 0 : undefined,
              borderBottomRightRadius: s === 'all' ? 0 : undefined,
              borderTopLeftRadius: s === 'issues' ? 0 : undefined,
              borderBottomLeftRadius: s === 'issues' ? 0 : undefined,
            }}
          >
            {s === 'all' ? 'All' : s === 'ready' ? 'Ready' : 'Issues'}
          </button>
        ))}
      </div>
      <span className="muted small" style={{ marginLeft: 'auto' }}>
        {shown === total ? `${total} markets` : `${shown} of ${total}`}
      </span>
    </div>
  );
}

/**
 * Phase 2 - Generate Environment Links. Pulls the per-jurisdiction launch
 * URL + guide bundle from the server and renders a filterable table with
 * one row per market. The server applies the studio's recipe (if one
 * exists) and falls back to raw Game task data when there is no recipe
 * yet, so the panel never goes blank.
 *
 * NOTE: this panel still lives under `features/phase4/` for git-history
 * reasons; the visible step number changed to 2 when the rail was
 * reordered. See `App.tsx` for the URL <-> folder mapping.
 */
export function EnvLinksPanel() {
  const { id } = useParams<{ id: string }>();
  const envLinks = useRunStore((s) => s.envLinks);
  const loading = useRunStore((s) => s.envLinksLoading);
  const error = useRunStore((s) => s.envLinksError);
  const loadEnvLinks = useRunStore((s) => s.loadEnvLinks);
  const refreshEnvLinks = useRunStore((s) => s.refreshEnvLinks);

  const [filters, setFilters] = useState<FiltersState>({ search: '', status: 'all' });
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!id) return;
    void loadEnvLinks(id);
  }, [id, loadEnvLinks]);

  const summary = useMemo(() => {
    if (!envLinks) return null;
    const total = envLinks.jurisdictions.length;
    const ready = envLinks.jurisdictions.filter((j) => j.status === 'ok').length;
    return { total, ready };
  }, [envLinks]);

  const filteredRows = useMemo(() => {
    if (!envLinks) return [];
    const q = filters.search.trim().toLowerCase();
    return envLinks.jurisdictions.filter((row) => {
      if (filters.status === 'ready' && row.status !== 'ok') return false;
      if (filters.status === 'issues' && row.status === 'ok') return false;
      if (!q) return true;
      const haystack = [
        row.jurisdiction,
        row.resolvedMarket,
        row.market,
        row.gameId,
        row.gameName,
        row.gameTaskKey,
        row.generatedUrl,
        row.playUrl,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [envLinks, filters]);

  /** Builds a single Markdown blob covering every visible row so the user
   * can paste the whole launch playbook into Slack / a Jira comment in one
   * shot. Honours the active filters so users can copy a subset. */
  const allMarkdown = useMemo(() => {
    if (!envLinks) return '';
    const parts: string[] = [];
    parts.push(`# ${envLinks.gameName ?? envLinks.studio} (${envLinks.texecKey})`);
    parts.push('');
    for (const row of filteredRows) {
      const head =
        row.resolvedMarket && row.resolvedMarket !== row.jurisdiction
          ? `${row.jurisdiction} (via ${row.resolvedMarket})`
          : row.jurisdiction;
      parts.push(`## ${head}`);
      if (row.gameTaskKey) parts.push(`Source: ${row.gameTaskKey}`);
      if (row.generatedUrl) parts.push(`URL: ${row.generatedUrl}`);
      else if (row.playUrl) parts.push(`Raw Play URL: ${row.playUrl}`);
      if (row.guideMd) {
        parts.push('');
        parts.push(row.guideMd);
      }
      parts.push('');
    }
    return parts.join('\n');
  }, [envLinks, filteredRows]);

  const toggleRow = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (loading && !envLinks) {
    return (
      <div className="flex-col">
        <div className="card phase-action-card">
          <h2 style={{ margin: 0 }}>Phase 2 - Generate Environment Links</h2>
          <p className="muted small" style={{ marginTop: 4 }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !envLinks) {
    return (
      <div className="flex-col">
        <div className="card phase-action-card">
          <h2 style={{ margin: 0 }}>Phase 2 - Generate Environment Links</h2>
          <p style={{ color: 'var(--bad)', marginTop: 8 }}>{error}</p>
          <button
            className="btn small"
            onClick={() => id && void loadEnvLinks(id, { force: true })}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!envLinks) {
    return (
      <div className="flex-col">
        <div className="card phase-action-card">
          <h2 style={{ margin: 0 }}>Phase 2 - Generate Environment Links</h2>
          <p className="muted small">No data yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-col">
      <div className="card phase-action-card">
        <div className="row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0 }}>Phase 2 - Generate Environment Links</h2>
            <p className="muted small" style={{ margin: '4px 0 0' }}>
              <strong>{envLinks.studio}</strong>
              {envLinks.gameName && <> &middot; {envLinks.gameName}</>}
              {envLinks.orderKey && (
                <>
                  {' '}&middot; Order{' '}
                  <JiraKeyLink keyId={envLinks.orderKey} className="badge mono small" />
                </>
              )}
              {' '}&middot; TEXEC{' '}
              <JiraKeyLink keyId={envLinks.texecKey} className="badge mono small" />
              {summary && (
                <>
                  {' '}&middot; {summary.ready}/{summary.total} markets ready
                </>
              )}
            </p>
          </div>
          <span className="spacer" />
          <button
            type="button"
            className="btn small"
            onClick={() => id && void refreshEnvLinks(id)}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh from Jira'}
          </button>
          <CopyButton text={allMarkdown} label="Copy all (markdown)" />
        </div>

        {envLinks.meta?.stale && (
          <p
            className="small"
            style={{
              marginTop: 8,
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid rgba(210, 153, 34, 0.4)',
              background: 'rgba(210, 153, 34, 0.08)',
              color: 'var(--warn)',
            }}
            title={envLinks.meta.staleReason}
          >
            {envLinks.meta.staleReason ?? 'Showing cached data; live Jira fetch failed.'}
          </p>
        )}

        {envLinks.recipeStatus !== 'ok' && (
          <p
            className="small"
            style={{
              marginTop: 8,
              padding: '8px 10px',
              borderRadius: 6,
              border:
                envLinks.recipeStatus === 'invalid'
                  ? '1px solid rgba(213, 0, 0, 0.4)'
                  : '1px solid rgba(80, 140, 220, 0.4)',
              background:
                envLinks.recipeStatus === 'invalid'
                  ? 'rgba(213, 0, 0, 0.08)'
                  : 'rgba(80, 140, 220, 0.08)',
              color:
                envLinks.recipeStatus === 'invalid' ? 'var(--bad)' : 'var(--text-strong)',
            }}
          >
            <strong>
              {envLinks.recipeStatus === 'invalid'
                ? 'Recipe invalid: '
                : 'No recipe yet for this studio. '}
            </strong>
            {envLinks.recipeError ?? null}
            <br />
            <span className="muted">
              Open <Link to="/settings/studios">Studios</Link> to draft or edit
              the recipe for <code>{envLinks.studioSlug}</code>, then refresh.
            </span>
          </p>
        )}
      </div>

      {envLinks.jurisdictions.length === 0 ? (
        <div className="card">
          <p className="muted">No jurisdictions are listed on this TEXEC.</p>
        </div>
      ) : (
        <div className="card">
          <FilterBar
            filters={filters}
            onChange={setFilters}
            total={envLinks.jurisdictions.length}
            shown={filteredRows.length}
          />
          <div className="data-table-wrap env-link-table-wrap">
            <table className="data-table env-link-table">
              <colgroup>
                <col style={{ width: 110 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 200 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 70 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 80 }} />
                <col />
                <col style={{ width: 200 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Market</th>
                  <th>Status</th>
                  <th>Game</th>
                  <th>Game ID</th>
                  <th>RTP</th>
                  <th>Currency</th>
                  <th>Lang</th>
                  <th>URL</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={9}>
                      <p className="muted small" style={{ margin: 0, padding: '8px 0' }}>
                        No rows match the current filters.
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => (
                    <EnvLinkTableRow
                      key={row.jurisdiction}
                      row={row}
                      expanded={expanded.has(row.jurisdiction)}
                      onToggle={() => toggleRow(row.jurisdiction)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
